import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React, { FunctionComponent } from 'react';

import {
  CurrentEventQuery,
  Milestone,
} from '../../../../../apollo/__generated__/graphql';
import { ForcePaymentDialog } from '../ForcePaymentDialog/ForcePaymentDialog';
import { PayWithHelloAssoButton } from '../PayWithHelloAssoButton/PayWithHelloAssoButton';
import { useOnlinePaymentCard } from './useOnlinePaymentCard';

interface OnlinePaymentCardProps {
  event: NonNullable<CurrentEventQuery['currentEvent']>;
  milestone: Milestone;
  payForUserId?: string;
}

export const OnlinePaymentCard: FunctionComponent<OnlinePaymentCardProps> = ({
  event,
  milestone,
  payForUserId,
}) => {
  const {
    onPayWithHelloAsso,
    totalPrice,
    isNoAlcool,
    onAlcoolChange,
    setVoucherText,
    onSubmitVoucher,
    activeVoucher,
    isForceDialogVisible,
    onCloseForcePaymentDialog,
    onForcePayment,
    numberOfDaysOptions,
    numberOfDays,
    onNumberOfDaysChange,
    arrivalDay,
    onArrivalDayChange,
    arrivalDayOptions,
    getDayNameByDay,
  } = useOnlinePaymentCard(event, milestone, payForUserId);

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6">{'Paiement par HelloAsso'}</Typography>
        <Typography variant="body1">
          {'Vous pouvez payer de façon sécurisé avec HelloAsso.'}
        </Typography>
      </CardContent>
      <CardActions sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
        {numberOfDaysOptions.length > 1 ? (
          <Box sx={{ mx: 1, mb: 2 }}>
            <ToggleButtonGroup
              color="secondary"
              exclusive
              onChange={(_, value) => onNumberOfDaysChange(value)}
              value={numberOfDays}
              defaultValue={0}
            >
              {numberOfDaysOptions.map((day) => (
                <ToggleButton key={day} value={day}>
                  {day + 1} soir{day > 0 ? 's' : ''}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        ) : null}
        {numberOfDays < milestone.prices.length - 1 ? (
          <Box sx={{ mx: 1, mb: 2 }}>
            <ToggleButtonGroup
              color="secondary"
              exclusive
              onChange={(_, value) => onArrivalDayChange(value)}
              value={arrivalDay}
              defaultValue={0}
            >
              {arrivalDayOptions.map((day) => (
                <ToggleButton key={day} value={day}>
                  {`Arriver le ${getDayNameByDay(day)}`}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        ) : null}
        {milestone.noAlcoholDiscount ? (
          <Box sx={{ mx: 1, mb: 2 }}>
            <ToggleButtonGroup
              color="secondary"
              exclusive
              onChange={(_, value) => onAlcoolChange(value)}
              value={isNoAlcool ? 'no-alcool' : 'alcool'}
              defaultValue="alcool"
            >
              <ToggleButton value="alcool">Avec Alcool</ToggleButton>
              <ToggleButton value="no-alcool">
                Bacchus Troué (-{milestone.noAlcoholDiscount / 100}€)
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        ) : null}
        {milestone.bedroomValue ? (
          <Box sx={{ mx: 1, mb: 2 }}>
            <Typography>
              {`Le paiement de la chambre s'effectue après le paiement de la place, dans un second temps. Une place en chambre est de ${milestone.bedroomValue / 100}€`}
            </Typography>
          </Box>
        ) : null}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            mb: 2,
          }}
        >
          {activeVoucher ? (
            <Typography>
              {`Code "${activeVoucher.code}" appliqué: -${activeVoucher.amount / 100}€`}
            </Typography>
          ) : (
            <>
              <TextField
                margin="normal"
                label="Code de réduction"
                type="text"
                id="voucher"
                onChange={(event) => setVoucherText(event.target.value)}
                onSubmit={onSubmitVoucher}
                onBlur={onSubmitVoucher}
              />
              <Box sx={{ mx: 2, mt: 1 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={onSubmitVoucher}
                >
                  Appliquer
                </Button>
              </Box>
            </>
          )}
        </Box>
        <PayWithHelloAssoButton
          amount={totalPrice}
          onClick={onPayWithHelloAsso}
        />
      </CardActions>
      <ForcePaymentDialog
        onCancel={onCloseForcePaymentDialog}
        onForce={onForcePayment}
        open={isForceDialogVisible}
      />
    </Card>
  );
};
